import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'ts-debounce';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { handleDownload } from 'src/helpers/helpers';
import { SelectOption } from 'src/UIKit';
import { useApplicationsService } from 'src/modules/applications/services';
import { GridSortModel } from '@mui/x-data-grid';
import { useAdminSignupsApi, SignupsPeriod } from '../api';
import { AdminSignupListItem, SignupConversion, SignupsFilter } from '../types';

export type SignupsTimePeriod = 'last7days' | 'last30days' | 'last90days' | 'custom';

const PERIOD_DAYS: Record<Exclude<SignupsTimePeriod, 'custom'>, number> = {
  last7days: 7,
  last30days: 30,
  last90days: 90,
};

// The two columns the API can order by, in the words the grid uses for them.
const SORTABLE_FIELDS: Record<string, 'date_created' | 'date_last_activity'> = {
  dateCreated: 'date_created',
  dateLastActivity: 'date_last_activity',
};

const DEFAULT_FILTER: SignupsFilter = {
  query: '',
  statuses: [],
  steps: [],
  applicationIds: [],
  source: '',
  sort: 'date_created_desc',
  limit: 25,
  offset: 0,
};

// Long enough that an email typed at speed is one request rather than twenty, short enough that the table
// answers while the admin is still looking at it. The same wait the companies list uses.
const SEARCH_DEBOUNCE_MS = 500;

const EXPORT_ERROR_MESSAGE = 'Could not export signups. Please try again.';

export const useAdminSignupsViewController = () => {
  const api = useAdminSignupsApi();
  const { list: applicationList, getApplications } = useApplicationsService();

  const [timePeriod, setTimePeriod] = useState<SignupsTimePeriod>('last30days');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'days').startOf('day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf('day'));
  const [filter, setFilter] = useState<SignupsFilter>(DEFAULT_FILTER);
  // What the box holds, against what the list was asked for: they differ while the typing settles.
  const [queryInput, setQueryInput] = useState('');

  const [signups, setSignups] = useState<AdminSignupListItem[]>([]);
  const [count, setCount] = useState(0);
  const [conversion, setConversion] = useState<SignupConversion | null>(null);
  // Busy from the first frame: starting at `false` flashes "no signups" before the first request has left.
  const [isLoading, setLoading] = useState(true);
  const [isExporting, setExporting] = useState(false);

  // One value for both halves, so the table and the summary can never be showing two different periods.
  const period = useMemo<SignupsPeriod>(
    () => ({ startDate: startDate, endDate: endDate }),
    [startDate, endDate],
  );

  /**
   * A search by text looks past the period; browsing it stays inside. The period is a month by default and a
   * funnel expires after a month of silence, so the funnels the abandonment alerts are about are precisely
   * the ones a bounded search would miss — and somebody arriving from an alert is searching, not browsing.
   */
  const searchPeriod = filter.query.length > 0 ? null : period;

  // A narrowed filter and a widened one can be in flight at once, and the slower answer would otherwise land
  // under controls that have moved on.
  const latestSearch = useRef(0);

  useEffect(() => {
    getApplications();
  }, [getApplications]);

  useEffect(() => {
    const request = latestSearch.current + 1;
    latestSearch.current = request;
    setLoading(true);

    api
      .searchSignups(filter, searchPeriod)
      .then(({ data }) => {
        if (latestSearch.current != request) return;
        setSignups(data.items);
        setCount(data.count);
      })
      .catch(() => {
        if (latestSearch.current != request) return;
        setSignups([]);
        setCount(0);
      })
      .finally(() => {
        if (latestSearch.current == request) setLoading(false);
      });
  }, [api, filter, searchPeriod]);

  // Only the period and the application: the conversion answers "what happened over this time", and
  // re-counting it for a status filter would report a different thing under the same heading.
  useEffect(() => {
    api
      .getConversion(period, filter.applicationIds[0])
      .then(({ data }) => setConversion(data))
      .catch(() => setConversion(null));
  }, [api, period, filter.applicationIds]);

  const changePeriod = useCallback((next: SignupsTimePeriod) => {
    setTimePeriod(next);
    if (next == 'custom') return;

    setStartDate(dayjs().subtract(PERIOD_DAYS[next], 'days').startOf('day'));
    setEndDate(dayjs().endOf('day'));
  }, []);

  const changeStartDate = useCallback((date: Dayjs | null) => {
    if (date == null) return;
    setTimePeriod('custom');
    setStartDate(date.startOf('day'));
  }, []);

  const changeEndDate = useCallback((date: Dayjs | null) => {
    if (date == null) return;
    setTimePeriod('custom');
    setEndDate(date.endOf('day'));
  }, []);

  // Back to the first page on every change: a narrowed set is usually shorter than the page the admin was on.
  const changeFilter = useCallback((over: Partial<SignupsFilter>) => {
    setFilter(current => ({ ...current, ...over, offset: 0 }));
  }, []);

  const applyQuery = useMemo(
    () => debounce((query: string) => changeFilter({ query: query }), SEARCH_DEBOUNCE_MS),
    [changeFilter],
  );

  const changeQuery = useCallback(
    (query: string) => {
      setQueryInput(query);
      void applyQuery(query);
    },
    [applyQuery],
  );

  const changeSort = useCallback((model: GridSortModel) => {
    const [first] = model;
    const field = first == null ? undefined : SORTABLE_FIELDS[first.field];
    // A cleared sort and a column the API cannot order by both mean the same thing: back to the default.
    const sort = field == null || first?.sort == null
      ? DEFAULT_FILTER.sort
      : (`${field}_${first.sort}` as SignupsFilter['sort']);

    setFilter(current => ({ ...current, sort: sort, offset: 0 }));
  }, []);

  const changePage = useCallback((page: number) => {
    setFilter(current => ({ ...current, offset: page * current.limit }));
  }, []);

  const changePageSize = useCallback((limit: number) => {
    setFilter(current => ({ ...current, limit: limit, offset: 0 }));
  }, []);

  // `searchPeriod`, not `period`: the file is what the table was asked for, without its pages.
  const exportSignups = useCallback(async() => {
    setExporting(true);
    try {
      handleDownload(await api.exportSignups(filter, searchPeriod));
    } catch (e) {
      reportErrorToUser(e, EXPORT_ERROR_MESSAGE);
    } finally {
      setExporting(false);
    }
  }, [api, filter, searchPeriod]);

  const applicationOptions = useMemo<SelectOption[]>(
    () => (applicationList?.items ?? []).map(item => ({ value: item.id, title: item.name })),
    [applicationList],
  );

  return {
    timePeriod,
    queryInput,
    changeQuery,
    startDate,
    endDate,
    filter,
    signups,
    count,
    conversion,
    isLoading,
    isExporting,
    applicationOptions,
    page: filter.limit > 0 ? Math.floor(filter.offset / filter.limit) : 0,
    changePeriod,
    changeStartDate,
    changeEndDate,
    changeFilter,
    changeSort,
    changePage,
    changePageSize,
    exportSignups,
  };
};
